import { useCallback, useRef, useState } from 'react'
import { createWidget, moveItem, resetWidget, sameWidgets } from '../layout/model'
import type { WidgetConfig, WidgetType } from '../types'

type Draft = { widgets: WidgetConfig[]; savedAt: Date }

/** Kaydedilmemiş düzen, sayfadan çıkılıp dönüldüğünde kaybolmasın diye oturum boyunca bellekte tutulur. */
const drafts = new Map<string, Draft>()

export function clearDraft(userId: string) {
  drafts.delete(userId)
}

export function useLayoutEditor(userId: string, saved: WidgetConfig[]) {
  const [initialDraft] = useState(() => drafts.get(userId))
  const [widgets, setWidgetsState] = useState<WidgetConfig[]>(() => initialDraft?.widgets ?? saved)
  const [draftAt, setDraftAt] = useState<Date | null>(() => initialDraft?.savedAt ?? null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const current = useRef(widgets)

  const setWidgets = useCallback(
    (next: WidgetConfig[] | ((prev: WidgetConfig[]) => WidgetConfig[])) => {
      const value = typeof next === 'function' ? next(current.current) : next
      if (value === current.current) return
      current.current = value
      const at = new Date()
      drafts.set(userId, { widgets: value, savedAt: at })
      setWidgetsState(value)
      setDraftAt(at)
    },
    [userId],
  )

  const add = useCallback(
    (type: WidgetType, index?: number) => {
      const widget = createWidget(type)
      setWidgets((prev) => {
        if (prev.some((w) => w.type === type)) return prev
        const next = prev.slice()
        next.splice(index ?? next.length, 0, widget)
        return next
      })
      setSelectedId(widget.id)
    },
    [setWidgets],
  )

  const remove = useCallback(
    (id: string) => {
      setWidgets((prev) => prev.filter((w) => w.id !== id))
      setSelectedId((cur) => (cur === id ? null : cur))
    },
    [setWidgets],
  )

  const update = useCallback(
    (id: string, patch: Partial<Omit<WidgetConfig, 'id' | 'type'>>) =>
      setWidgets((prev) => prev.map((w) => (w.id === id ? { ...w, ...patch } : w))),
    [setWidgets],
  )

  const reset = useCallback((id: string) => setWidgets((prev) => prev.map((w) => (w.id === id ? resetWidget(w) : w))), [setWidgets])

  const move = useCallback(
    (activeId: string, overId: string) =>
      setWidgets((prev) => moveItem(prev, prev.findIndex((w) => w.id === activeId), prev.findIndex((w) => w.id === overId))),
    [setWidgets],
  )

  return {
    widgets,
    setWidgets,
    selectedId,
    select: setSelectedId,
    selected: widgets.find((w) => w.id === selectedId) ?? null,
    draftAt,
    dirty: !sameWidgets(widgets, saved),
    add,
    remove,
    update,
    reset,
    move,
  }
}

export type LayoutEditor = ReturnType<typeof useLayoutEditor>
